/*
 **  This file has been migrated to the new translated structure.
 **  1) All strings must be added with the correct casing.
 **  2) New strings must be added to the the en_US_migrated.json.
 */

import moment from "moment";
import * as d3 from "d3";
import themes from "../settings/themes";
import { siteConfig } from "../settings/config";
import { notification } from "antd";

export function getGreeting() {
  const currentHour = moment().hour();
  return currentHour >= 12 && currentHour <= 17
    ? "Good afternoon"
    : currentHour >= 18
    ? "Good evening"
    : "Good morning";
}

export function checkExists(obj, level, ...rest) {
  if (obj === undefined) return false;
  if (rest.length === 0 && obj.hasOwnProperty(level)) return true;
  return checkExists(obj[level], ...rest);
}

export function getValue(path, obj) {
  const get = (p, o) => p.reduce((xs, x) => (xs && xs[x] !== undefined ? xs[x] : null), o);
  let fields = path.split(".");
  return get(fields, obj);
}

export function formatNumber(value, format, defaultvalue = null) {
  let result = defaultvalue ?? "--";

  if (value !== null && !isNaN(value)) {
    result = d3.format(format)(value);
  } else if (defaultvalue !== null && !isNaN(defaultvalue)) {
    result = d3.format(format)(defaultvalue);
  }
  return result;
}

export function charScrubber(str) {
  const percent = "\u0025";
  const semicolon = "\u003B";
  const amper = "\u0026";
  const cash = "\u0024";
  let output = str.replace(/[%]/g, percent).replace(/[;]/g, semicolon).replace(/[&]/g, amper).replace(/[$]/g, cash);
  return output;
}

/**
 * Trim white space off of the beginning and end of each value
 * @param {object} data
 * @returns {object} data modified to have trimmed values
 */
export function trimFields(data) {
  let temp = { ...(data || {}) };
  for (const [key, value] of Object.entries(temp)) {
    temp[key] = typeof value === "string" ? value.trim() : value;
    if (typeof value === "string") {
      temp[key] = value.trim().replace(/\s{2,}/g, " ");
    }
  }
  return temp;
}

function hashCode(str) {
  /* Helper function for generateColor */
  if (!str) return 0;
  var hash = 0,
    i,
    chr;
  for (i = 0; i < str.length; i++) {
    chr = str.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
}

export function generateColor(str, colors, tag = false, level = "-5") {
  const colorPalettes = ["red", "volcano", "orange", "gold", "blue", "cyan", "green", "geekblue", "purple", "magenta"];
  if (!colors) {
    colors = colorPalettes;
  }
  var hash = hashCode(str);
  let colorName = colors[Math.abs(hash) % colors.length];
  if (tag) {
    return colorName;
  }
  return themes[siteConfig.defaultTheme].color[colorName][colorName + level];
}

export function utf8_to_b64(str) {
  return window.btoa(unescape(encodeURIComponent(str)));
}

export function b64_to_utf8(str) {
  return decodeURIComponent(escape(window.atob(str)));
}

export function similarity(s1, s2) {
  // Gets similarity based off Levenshtein distance
  // https://en.wikipedia.org/wiki/Levenshtein_distance
  // Bascially it calculates the simiularity score based
  // on how many edits it takes to make the two strings equal

  if (!s1 || !s2) {
    // If either are null the sim score is 0
    return 0.0;
  }
  var longer = s1;
  var shorter = s2;
  if (s1.length < s2.length) {
    longer = s2;
    shorter = s1;
  }
  var longerLength = longer.length;
  if (longerLength === 0) {
    return 1.0;
  }
  return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
}

function editDistance(s1, s2) {
  s1 = s1.toLowerCase();
  s2 = s2.toLowerCase();

  var costs = [];
  for (var i = 0; i <= s1.length; i++) {
    var lastValue = i;
    for (var j = 0; j <= s2.length; j++) {
      if (i === 0) costs[j] = j;
      else {
        if (j > 0) {
          var newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
    }
    if (i > 0) costs[s2.length] = lastValue;
  }
  return costs[s2.length];
}

export const objectEquals = (obj0, obj1) => {
  if (obj0 === null || obj0 === undefined || obj1 === null || obj1 === undefined) {
    return obj0 === obj1;
  }
  // if they are functions, they should exactly refer to same one (because of closures)
  // if they are regexps, they should exactly refer to same one
  if (obj0 instanceof Function || obj0 instanceof RegExp) {
    return obj0 === obj1;
  }
  if (obj0 === obj1 || obj0.valueOf() === obj1.valueOf()) {
    return true;
  }
  // if they are dates, they must equal valueOf
  // if they are strictly equal, they both need to be objects
  if (
    (Array.isArray(obj0) && obj0.length !== obj1.length) ||
    obj0 instanceof Date ||
    !(obj0 instanceof Object) ||
    !(obj1 instanceof Object)
  ) {
    return false;
  }

  // recursive object equality check
  let props = Object.keys(obj0);
  return (
    Object.keys(obj1).every(function (i) {
      return props.indexOf(i) !== -1;
    }) &&
    props.every(function (i) {
      return objectEquals(obj0[i], obj1[i]);
    })
  );
};

export function overwriteObj(obj, overwrite) {
  let temp = { ...(obj || {}) };
  Object.keys(overwrite || {}).forEach(key => {
    if (!temp.hasOwnProperty(key) || temp[key]?.constructor !== Object) {
      temp[key] = overwrite[key];
    } else {
      temp[key] = overwriteObj(temp[key], overwrite[key]);
    }
  });
  return temp;
}

export const hasSpecialChar = val => {
  return /.*[-!$%^&*()_+|~=`{}[\]:";'<>?,./@#]{1,}.*/.test(val);
};

export function makeFourlyticsCustomVariables(values, context = {}) {
  let customVars = {};
  const parser = {
    text: value => {
      if (value?.text !== undefined && value?.text !== null) {
        return { value: value.text };
      } else {
        return false;
      }
    },
    state: value => {
      if (value?.state !== undefined && value?.state !== null) {
        const temp = value.state.split(".");
        let tempValue = JSON.parse(JSON.stringify(context));
        for (const item of temp) {
          if (Object.keys(tempValue).includes(item)) {
            tempValue = tempValue?.[item];
          } else {
            return false;
          }
        }
        if (tempValue !== undefined && tempValue !== null) {
          return { value: tempValue };
        }
      } else {
        return false;
      }
    },
    func: value => {
      if (value?.func !== undefined && value?.func !== null && value?.func?.call) {
        const params = value?.func?.params?.split(",") ?? [];
        let in_params = [];
        let tempValue = JSON.parse(JSON.stringify(context));
        for (const param of params) {
          const the_param = param?.split(".");
          if (the_param[0] === "state") {
            const temp = value?.func?.[the_param?.[1]]?.split(".") ?? [];
            for (const item of temp) {
              if (Object.keys(tempValue).includes(item)) {
                tempValue = tempValue?.[item];
              } else {
                return false;
              }
            }
          } else {
            tempValue = param;
          }
          if (tempValue !== undefined && tempValue !== null) {
            in_params.push(tempValue);
          } else {
            console.warn("There was an issue parsing");
            return false;
          }
        }
        return { value: context?.[value.func.call]?.(...in_params) };
      } else {
        return false;
      }
    },
    default: () => false,
  };
  for (let value of values) {
    let new_var = parser?.[value?.type ?? "default"](value);
    if (new_var?.value && value?.key) {
      customVars[value?.key] = new_var?.value;
    }
  }
  return customVars;
}

export function debounce(func, time = 250) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, time);
  };
}

export function formatSymbolInfo(item) {
  if (item?.entity_id === undefined && item?.category === undefined) {
    return false;
  } else {
    return (
      (item?.entity_id === null ? "--" : item?.entity_id) + " | " + (item?.category === null ? "--" : item?.category)
    );
  }
}

export function getPrimaryAddressKey(addresses, key = "prov_state") {
  let foundKey = null;
  for (const address of addresses || []) {
    if (address && address?.is_primary && address?.[key]) {
      foundKey = address[key];
      break;
    }
  }
  return foundKey;
}

export function findClosestProcessDate(dates, processDate) {
  let newDate = processDate;
  const tempProcessDate = moment(processDate);
  const maxDate = moment(dates?.max_file_date);
  const minDate = moment(dates?.min_file_date);
  if (tempProcessDate.diff(maxDate) < 0 && tempProcessDate.diff(minDate) > 0) {
    const tempArray = [...dates.excluded_dates];
    const filteredArray = tempArray.filter(date => moment(date).diff(tempProcessDate) <= 0);
    let count = 0;
    let found = false;
    while (count < 36500) {
      //Checks up to approximately 100 years of previous days before it automatically uses the min_file_date.
      if (filteredArray.includes(newDate)) {
        newDate = moment(newDate).subtract(1, "days").format("YYYY-MM-DD");
      }
      if (moment(newDate).diff(minDate) <= 0) {
        newDate = dates?.min_file_date;
        found = true;
        break;
      }
      count++;
    }
    if (!found) {
      newDate = dates?.min_file_date;
    }
  } else {
    if (tempProcessDate.diff(maxDate) === 0) {
      newDate = dates?.max_file_date;
    } else {
      newDate = dates?.min_file_date;
    }
  }
  return newDate;
}

export function addAllToSet(set, arr) {
  for (let el of arr) {
    set.add(el);
  }
  return set;
}

// ES6 has a set difference method as of Feb 2024.
// However I don't want to use it as I don't want to rely on it
// Since some people might not update right away and it's not supported in all browsers yet
// Jacob Shaw
export function setDifference(a, b) {
  return new Set(Array.from(a).filter(item => !b.has(item)));
}

// Similar situation as above - Jacob Shaw
export function setIntersection(a, b) {
  return new Set(Array.from(a).filter(item => b.has(item)));
}

// Converts an exception from graphQL
// This is a temporary function to ensure backwards compatibility
// Once everything is moved over to graphQL this shouldn't be needed anymore
// Jacob Shaw
export function convertTransactionException(excep, exceptionTypes, exceptionCategories, accountInfo, intlFormatKeys) {
  let exception_type = exceptionTypes[excep.exception_type_id];
  let exception_category = exceptionCategories[exception_type.exception_cat_id];
  return {
    account_uuid: accountInfo?.node_uuid,
    exception_cat_id: exception_type.exception_cat_id,
    exception_class_id: exception_type.exception_class_id,
    exception_id: excep.node_uuid,
    euuid: excep.node_uuid,
    exception_type_id: excep.node_type_id,
    file_date: accountInfo?.file_date,
    state_json: {},
    catname: intlFormatKeys(
      exception_category?.iso_language ?? {},
      exception_category?.iso_lang_keys ?? [],
      exception_category?.description ?? "--"
    ),
    typename: intlFormatKeys(exception_type?.iso_language, exception_type?.iso_lang_keys, "--"),
    exception_json: {
      exception_details: {
        trantrades: excep?.trade?.concat(excep?.transaction ?? [])?.map(t => t.node_uuid) ?? [],
      },
    },
  };
}

// Converts an exception from graphQL
// This is a temporary function to ensure backwards compatibility
// Once everything is moved over to graphQL this shouldn't be needed anymore
// Jacob Shaw
export function convertTranTrades(trantrade, accountInfo, intlFormatKeys) {
  return {
    trantrade_uuid: trantrade.node_uuid,
    account_attrs: {
      account_id: accountInfo?.node_id,
    },
    ...trantrade,
    entry_description: intlFormatKeys(
      trantrade?.security?.iso_language ?? {},
      trantrade?.security?.iso_lang_keys ?? [],
      "--"
    ),
    commission: trantrade?.commission?.CAD?.fef_comm_amt,
  };
}

export function formatLookups(lookup_payload) {
  let lookup_keys = {
    exception_types: "exception_type_id",
    exception_categories: "exception_cat_id",
    investment_objective: "inv_objective_cd",
    entry_type: "fef_entry_type_id",
    security_categories: "fef_sec_cat_cd",
    investment_mix: "inv_mix_cd",
    risk_tolerance: "fef_risk_tolerance_cd",
    node_type: "description",
    exception_state: "fef_exception_state_id",
    enhanced_supervision_type: "en_sup_type_uuid",
    objective_code: "objective_code",
    sector: "fef_allocation_code",
  };

  let lookup_map = {};

  for (const [key, value] of Object.entries(lookup_payload)) {
    lookup_map[key] = {};
    for (const lookup of value) {
      lookup_map[key][lookup[lookup_keys[key]]] = lookup;
    }
  }
  return lookup_map;
}

export function mergeHoldingValuation(object) {
  if (!object?.holdings || !object?.holding_valuation) {
    return object;
  }

  const { holdings, holding_valuation, ...otherFields } = object;

  const mapping = holding_valuation.reduce((valuations, item) => {
    if (!valuations[item.security_uuid]) {
      valuations[item.security_uuid] = {};
    }
    valuations[item.security_uuid][item.iso_currency_cd] = item;
    return valuations;
  }, {});

  const mergedHoldings = holdings.map(holding => ({
    ...holding,
    valuation: mapping[holding.security_uuid] || {},
  }));

  return { ...otherFields, mergedHoldings };
}

export function convertValuation(object) {
  const { valuation, ...otherFields } = object;
  let mapping = valuation;
  if (valuation) {
    mapping = valuation.reduce((valuations, item) => {
      valuations[item.iso_currency_cd] = item;
      return valuations;
    }, {});
  }
  return { ...otherFields, valuation: mapping };
}
/*
Generic error handler for non specific api calls
Checks for custom code/error message
Displays notification for error
*/
export function errorHandler({ error, intl, code, message }) {
  let errDisplay = null;
  let eCode = error?.data?.status_code;
  if (code) {
    // Check if specfic message for specific code passed in
    if (eCode === code && message) {
      notification.error({
        message: intl.formatMessage({ id: message }),
        duration: 0,
      });
      return;
    }
  }
  // If specific message for api call passed in
  if (message) {
    notification.error({
      message: intl.formatMessage({ id: message }),
      duration: 0,
    });
    return;
  }
  // No message or code, use defaults
  switch (eCode) {
    case 400:
      errDisplay = "error.bad_request";
      break;
    case 403:
      errDisplay = "error.forbidden";
      break;
    case 404:
      errDisplay = "error.not_found";
      break;
    case 409:
      errDisplay = "error.duplicate_key";
      break;
    case 500:
      errDisplay = "error.internal_error";
      break;
    case 504:
      errDisplay = "error.timeout";
      break;
    default:
      errDisplay = "Something went wrong. Review request";
      break;
  }
  notification.error({
    message: intl.formatMessage({ id: errDisplay }),
    duration: 0,
  });
}

export function insert_map(obj, key, val) {
  if (!obj) {
    return {};
  }
  if (key in obj) {
    obj[key].push(val);
  } else {
    obj[key] = [val];
  }
  return obj;
}
